import * as StationApi from '@/api/device/station'
import useAppStore from '@/store/modules/app'
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'

const appStore = useAppStore()


/**
 * 设备列表
 * @param {object} options 配置项
 * @param {boolean} options.showESSDevice 是否显示储能设备
 * @param {boolean} options.showOtherDevices 是否显示其他设备
 */
export function useDeviceList(options = {}) {
  const defaultOptions = {
    showESSDevice: true,
    showOtherDevices: false,
  }
  Object.assign(defaultOptions, options)

  const route = useRoute()
  const stationId = computed(() => route.query.stationId)

  const eLinkList = ref([])
  const outerMeters = ref([])
  const loading = ref(false)
  const items = ref([])


  // 电站信息
  const station = computed(() => appStore.getStationById(stationId.value))

  // 是否集中式
  const isCentralized = computed(() => station.value?.energyStorageType === 2)

  // 产品型号
  const productModel = computed(() => station.value?.equipmentModel?.split(',')[0] || '')


  let updatedResolve = () => { }
  let updatedReject = () => { }

  // 更新设备列表的 promise
  const updatedPromise = new Promise((resolve, reject) => {
    updatedResolve = resolve
    updatedReject = reject
  })

  /**
   * 根据 galaxy id 获取 galaxy
   * @param {string} uuid 
   * @returns {object} galaxy
   */
  const getGalaxyById = (uuid) => {
    for (const eLink of eLinkList.value) {
      if (eLink?.galaxy?.uuid === uuid) {
        return eLink.galaxy || {}
      }
    }
    return {}
  }

  /**
   * 根据 eLink id 获取 eLink
   * @param {string} uuid 
   * @returns {object} eLink
   */
  const getELinkById = (uuid) => {
    for (const eLink of eLinkList.value) {
      if (eLink?.uuid === uuid) {
        return eLink || {}
      }
    }
    return {}
  }



  /**
   * 根据 eBlock id 获取 eBlock
   * @param {string} uuid 
   * @returns {object} eBlock
   */
  const getEBlockById = (uuid) => {
    for (const eLink of eLinkList.value) {
      const eBlock = eLink?.eBlocks?.find(it => it.uuid === uuid)
      if (eBlock) {
        return eBlock || {}
      }
    }
    return {}
  }

  /**
   * 根据 galaxy id 获取 eLink
   * @param {string} uuid 
   * @returns {object} eLink
   */
  const getELinkByGalaxyId = (uuid) => {
    for (const eLink of eLinkList.value) {
      if (eLink?.galaxy?.uuid === uuid) {
        return eLink || {}
      }
    }
    return {}
  }


  /**
   * 根据 eBlock id 获取 eLink uuid
   * @param {string} uuid 
   * @returns {string} eLink uuid
   */
  const getELinkIdByEBlockId = (uuid, key='uuid') => {
    for (const eLink of eLinkList.value) {
      const eBlock = eLink?.eBlocks?.find(it => it.uuid === uuid)
      if (eBlock) {
        return eLink[key]
      }
    }
    return null
  }


  // 根据uuid获取设备类型
  const getDeviceTypeById = (uuid) => {
    if (!uuid) {
      return
    }
    const eLink = eLinkList.value.find(it => it.uuid === uuid)
    if (eLink) {
      return 'eLink'
    }

    for (const eLink of eLinkList.value) {
      const eBlock = eLink?.eBlocks?.find(it => it.uuid === uuid)
      if (eBlock) {
        return 'eBlock'
      }
    }

    const meter = outerMeters.value.find(it => it.uuid === uuid)
    if (meter) {
      return 'meter'
    }
  }

  const updateDeviceList = async (stationId) => {
    loading.value = true

    try {
      const res = await StationApi.v2.getDevicesByIdV2(stationId)
      updatedResolve(res)

      res.data.eLinks.forEach(elink => {
        if (Array.isArray(elink.eBlocks)) {
          elink.eBlocks.forEach(eblock => {
            eblock.data = {}
          })
        } else {
          elink.eBlocks = []
        }
      })

      eLinkList.value = res.data.eLinks
      outerMeters.value = res.data.outerMeters.map(meter => {
        return {
          ...meter,
          data: {}
        }
      })


      // 构建设备树
      const eLinks = []
      const otherDevices = []


      // 
      items.value = []

      // eLink
      for (const eLink of res.data.eLinks) {
        const firstChild = {
          id: eLink.uuid,
          title: eLink.eLinkName,
          type: 'elink',
          showId: true,
        }

        const eBlocks = eLink.eBlocks.map(eBlock => {
          return {
            id: eBlock.uuid,
            title: eBlock.eBlockName,
            type: 'eblock',
            showId: true,
            raw: eBlock
          }
        })

        const children = [firstChild, ...eBlocks]
        const title = isCentralized.value ? eLink.galaxy?.equipmentName : eLink.sort + '#储能方阵'//eLink.eLinkName

        eLinks.push({
          id: isCentralized.value ? eLink.galaxy?.uuid : 'array-' + eLink.uuid,
          title: title,
          children: isCentralized.value ? void 0 : children,
          type: isCentralized.value ? 'galaxy' : 'square',
          uuid: isCentralized.value ? eLink.galaxy?.uuid : eLink.uuid,
          showId: isCentralized.value,
          raw: eLink
        })
      }


      // 电表
      for (const meter of res.data.outerMeters) {
        otherDevices.push({
          id: meter.uuid,
          title: meter.meterName,
          type: 'meter',
          raw: meter,
          showId: true,
        })
      }

      if (defaultOptions.showESSDevice) {
        items.value.push({
          id: 'ess-device',
          title: '储能设备',
          children: eLinks
        })
      }

      if (otherDevices.length > 0 && defaultOptions.showOtherDevices) {
        items.value.push({
          id: 'other-device',
          title: '其他设备',
          children: otherDevices,
        })
      }

    } catch (error) {
      console.warn(error)
      updatedReject(error)
    }
    finally {
      loading.value = false
    }


  }

  // updateDeviceList(stationId.value)

  watch(() => stationId.value, (stationId, _, onCleanup) => {
    if (stationId) {
      updateDeviceList(stationId)
    }
  }, { immediate: true })



  return {
    eLinkList,
    outerMeters,
    loading,
    items,
    updatedPromise,
    getGalaxyById,
    getELinkById,
    getELinkByGalaxyId,
    getEBlockById,
    getELinkIdByEBlockId,
    updateDeviceList,
    getDeviceTypeById,
  }

}
